import React from 'react';
import ElapsedTimer from './ElapsedTimer';

/**
 * Sticky progress banner shown while async operations run.
 * progress: 0–100 for stepped progress; omit or null for indeterminate bar.
 * startedAt: epoch ms when the job began (shows elapsed timer beside the bar).
 */
export default function ActivityBanner({
  label,
  detail = '',
  progress = null,
  steps = [],
  onCancel = null,
  cancelLabel = 'Stop',
  warnings = [],
  startedAt = null,
}) {
  if (!label) return null;

  const indeterminate = progress == null;
  const visibleWarnings = (warnings || []).filter(Boolean);
  const visibleSteps = (steps || []).filter(Boolean);
  const showDetail = detail && detail.trim() && detail.trim().toLowerCase() !== label.trim().toLowerCase();

  return (
    <div
      className="shrink-0 bg-gray-950 border-b border-teal-500/30 px-6 py-3"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex items-start gap-3 max-w-5xl mx-auto">
        <svg
          className="animate-spin h-5 w-5 text-teal-400 shrink-0 mt-0.5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-teal-200 leading-snug">{label}</p>
              {showDetail && (
                <p className="text-xs text-teal-400/80 mt-0.5">{detail}</p>
              )}
            </div>
            {!indeterminate && (
              <span className="shrink-0 text-xs tabular-nums text-gray-400">{Math.round(progress)}%</span>
            )}
          </div>
          {visibleSteps.length > 0 && (
            <ul className="mt-2 space-y-1">
              {visibleSteps.map((step) => (
                <li
                  key={step.key}
                  className={`text-xs leading-snug ${
                    step.state === 'active'
                      ? 'text-teal-200'
                      : step.state === 'done'
                        ? 'text-gray-500'
                        : 'text-gray-600'
                  }`}
                >
                  <span className="mr-1.5" aria-hidden="true">
                    {step.state === 'done' ? '✓' : step.state === 'active' ? '●' : '○'}
                  </span>
                  <span className={step.state === 'done' ? 'line-through' : ''}>{step.label}</span>
                  {step.state === 'active' && step.message && (
                    <span className="text-gray-500"> — {step.message}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {visibleWarnings.length > 0 && (
            <ul className="mt-2 space-y-0.5">
              {visibleWarnings.slice(-2).map((warning) => (
                <li key={warning} className="text-xs text-amber-300/90" title={warning}>
                  {warning}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-2 flex items-center gap-3">
            <div className="flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden">
              {indeterminate ? (
                <div className="activity-bar-indeterminate h-full rounded-full bg-teal-500" />
              ) : (
                <div
                  className="h-full rounded-full bg-teal-500 transition-all duration-500 ease-out"
                  style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                />
              )}
            </div>
            {startedAt != null && <ElapsedTimer startedAt={startedAt} />}
          </div>
        </div>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg border border-red-500/40 text-red-300 hover:bg-red-950/40 transition-colors"
          >
            {cancelLabel}
          </button>
        )}
      </div>
    </div>
  );
}
